import {
  FloatingFormItem,
  Group,
  InputDecimal,
  SaveButton,
} from '@/components/_common';
import {
  TaxeIcmsOriginSelect,
  TaxeIcmsCstSelect,
  TaxeIcmsCsosnSelect,
  TaxePisCstSelect,
  TaxeCofinsCstSelect,
  TaxeIssqnSelect,
} from '@/components/_common/TaxesSelect';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import { Checkbox, Col, Form, Input, Row, Typography } from 'antd';
import { TaxCouponItemCofinsCst } from '@/core/domain/coupon/coupons/coupons.item.types';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import {
  CouponCompanyConfig,
  CouponCompanyTaskConfig,
} from '@/core/domain/coupon/company/company.types';
import { useCouponCompanyConfigTask } from '@/core/domain/coupon/company/company.hooks';
import CfopSelectField from '@/components/_common/CfopSelectField';
import { useEffect } from 'react';

interface Props {
  settings?: CouponCompanyConfig;
}

const CompanyCouponCFSForm: React.FC<Props> = ({ settings }) => {
  const [form] = Form.useForm<CouponCompanyTaskConfig>();
  const ruleRequired = useDefaultEmptyFieldError();

  const { company } = useCompanyFormContext();

  const couponCompanyConfigTask = useCouponCompanyConfigTask();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const hasSimpleNational = company?.taxRegimeId === 2;

  const taxesCofinsCst = Form.useWatch(
    ['defaultTaxConfig', 'cofins', 'cst'],
    form,
  );

  useEffect(() => {
    if (settings) {
      form.setFieldsValue({
        defaultTaxConfig: settings.defaultTaxConfig.taskConfig,
      });
    }
  }, [settings]);

  const optionalCst = [
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_NORMAL_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_DIFFERENTIATED_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_QUANTITY_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_SUBSTITUTION,
    TaxCouponItemCofinsCst.OTHER_OPERATIONS,
  ];

  const cstRequired = !!taxesCofinsCst && !optionalCst.includes(taxesCofinsCst);

  async function onSubmit(values: CouponCompanyTaskConfig) {
    try {
      if (company) {
        await couponCompanyConfigTask.mutateAsync({
          companyId: company?.id,
          ...values,
        });
      }
    } catch {}
  }

  return (
    <Form onFinish={onSubmit} form={form}>
      <Group position="between" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Configuração de tributação dos itens
        </Typography.Title>

        <SaveButton
          htmlType="submit"
          loading={couponCompanyConfigTask.isLoading}
        >
          Salvar
        </SaveButton>
      </Group>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Title level={5}>Geral</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'cfopId']}
                label="CFOP"
                rules={[ruleRequired]}
              >
                <CfopSelectField />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'unitMeasure']}
                label="Unidade"
                rules={[ruleRequired, defaultMaxLenghtFieldError(100)]}
              >
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>ISSQN</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'issqn', 'operationType']}
                label="Tipo de tributação"
                rules={[ruleRequired]}
              >
                <TaxeIssqnSelect />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'issqn', 'rate']}
                label="Alíquota(%)"
                rules={[ruleRequired]}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'issqn', 'deduction']}
                label="Dedução(%)"
                rules={[ruleRequired]}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <Form.Item
                name={['defaultTaxConfig', 'issqn', 'taxIncentive']}
                rules={[ruleRequired]}
                valuePropName="checked"
              >
                <Checkbox>Incentivo fiscal</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>ICMS</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'icms', 'origin']}
                label="Origem"
              >
                <TaxeIcmsOriginSelect allowClear />
              </FloatingFormItem>
            </Col>

            {!hasSimpleNational && (
              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['defaultTaxConfig', 'icms', 'cst']}
                  label="CST"
                >
                  <TaxeIcmsCstSelect allowClear />
                </FloatingFormItem>
              </Col>
            )}

            {hasSimpleNational && (
              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['defaultTaxConfig', 'icms', 'csosn']}
                  label="CSOSN"
                >
                  <TaxeIcmsCsosnSelect allowClear />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'icms', 'rate']}
                label="Alíquota(%)"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>PIS</Typography.Title>
          <Row gutter={16}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'pis', 'cst']}
                label="CST"
              >
                <TaxePisCstSelect allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'pis', 'rate']}
                label="Alíquota(%)"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>COFINS</Typography.Title>
          <Row gutter={16}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'cofins', 'cst']}
                label="CST"
              >
                <TaxeCofinsCstSelect allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'cofins', 'rate']}
                label="Alíquota(%)"
                rules={cstRequired ? [ruleRequired] : []}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CompanyCouponCFSForm;
